import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Snackbar, TextField,
} from '@mui/material';
import {
  buscarEmailProcessoByIdProcessoAndTipoDestinatarioService,
} from '../../../../services/core/processos/processosEmails';
import { enviarEmailComDocumentosProcessoService } from '../../../../services/core/processos/processoDocumentoService';
import { extractResponseErrorMessage, isValidationException } from '../../../../helpers/utils/form.utils';
import TableUtils from '../../../../helpers/utils/table.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível enviar o e-mail.';
// Mesmo enum/labels de `emailsTypes` real (`EnviarEmailDocumentoProcesso.jsx`).
const TIPOS_DESTINATARIO = [
  { id: 'EMAIL_CLIENTE', value: 'Cliente' },
  { id: 'EMAIL_DEVEDOR', value: 'Devedor' },
  { id: 'EMAIL_COLABORADOR_ABE', value: 'Outros' },
];
// Mesmas chaves de `enviarEmailDocumentoProcessoController.makeErrorFields`
// real — usado para decidir se um erro de validação do backend é exibível
// campo a campo ou cai no Snackbar genérico (mesmo critério já documentado
// no CLAUDE.md do módulo).
const CAMPOS_FORMULARIO = ['email', 'assunto', 'copia', 'corpoEmail'];

function makeFormFieldsEmail(emailUsuario) {
  return {
    tipoDestinatario: 'EMAIL_CLIENTE',
    email: '',
    copia: emailUsuario || '',
    assunto: '',
    corpoEmail: '',
  };
}

/**
 * Port de `EnviarEmailDocumentoProcesso.jsx`/`enviarEmailDocumentoProcessoController.js`
 * (tela real) para MUI v5 — AW2-1094 Etapa 6. Envia os documentos
 * selecionados na Etapa 2 (`allIdsDocumentos`, o Map inteiro passado pelo
 * botão "Enviar" do cabeçalho) por e-mail — `idProcessoDocumento` do payload
 * é sempre recalculado de `allIdsDocumentos` no momento do submit
 * (`TableUtils.getIdsSelected`, já reaproveitado), nunca uma cópia
 * snapshotada num state próprio.
 *
 * `email` (campo "Para") é auto-preenchido ao abrir (efeito próprio guardado
 * por `open`, mesmo padrão de `buscarEmailsPadraoDevedorAcordo` em
 * `DialogEnviarEmailAcordo.jsx`) E a cada troca de "Tipo do Destinatário"
 * (handler imperativo) via `buscarEmailProcessoByIdProcessoAndTipoDestinatarioService`
 * — pulado para "Outros" (`EMAIL_COLABORADOR_ABE`, mesma regra real). Guard
 * contra resposta tardia via `sessaoRef` (equivalente ao guard `ignore` já
 * documentado no módulo) cobre tanto reabertura do dialog quanto troca de
 * `idProcesso` em voo.
 *
 * "Cópia" pré-preenchido com o e-mail do usuário logado via
 * `useSelector(state => state.authentication.userInformation.email)` —
 * reativo, não `store.getState()` imperativo (reservado para código fora de
 * componente React, como `hasUserPermissions`).
 */
export default function DialogEnviarEmailDocumentoProcesso({
  open, idProcesso, allIdsDocumentos, onClose,
}) {
  const emailUsuario = useSelector(state => state.authentication.userInformation.email);

  const [formFields, setFormFields] = useState(() => makeFormFieldsEmail(emailUsuario));
  const [errosPorCampo, setErrosPorCampo] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setFormFields(makeFormFieldsEmail(emailUsuario));
    setErrosPorCampo({});
    setErro('');
    setEnviando(false);
  });

  // Guard contra resposta tardia via `sessaoRef` (equivalente ao guard
  // `ignore` já documentado no módulo) — cobre reabertura do dialog e troca
  // de `idProcesso` em voo. Definida DEPOIS da desestruturação de `sessaoRef`
  // acima (não dentro do `aoAbrir` do hook) para evitar `no-use-before-define`
  // sem sacrificar o guard.
  const buscarEmailPorTipo = (tipo) => {
    if (tipo === 'EMAIL_COLABORADOR_ABE') {
      return;
    }
    const sessaoAlvo = sessaoRef.current;
    buscarEmailProcessoByIdProcessoAndTipoDestinatarioService(idProcesso, tipo)
      .then(({ data }) => {
        if (sessaoRef.current === sessaoAlvo) {
          setFormFields(fields => ({ ...fields, email: data || '' }));
        }
      })
      .catch(() => {});
  };

  // Busca o e-mail padrão do tipo default ("Cliente") ao abrir — mesmo
  // padrão de `buscarEmailsPadraoDevedorAcordo` em `DialogEnviarEmailAcordo.jsx`
  // (efeito próprio guardado por `open`, não dentro do `aoAbrir` do hook).
  useEffect(() => {
    if (!open) {
      return;
    }
    buscarEmailPorTipo('EMAIL_CLIENTE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idProcesso]);

  const onChangeTipoDestinatario = (event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, tipoDestinatario: value, email: '' }));
    buscarEmailPorTipo(value);
  };

  const onChangeCampo = nome => (event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, [nome]: value }));
    setErrosPorCampo(erros => ({ ...erros, [nome]: undefined }));
  };

  const idsSelecionados = TableUtils.getIdsSelected(allIdsDocumentos);
  const totalDocumentos = idsSelecionados.length;
  const maisDeUmDocumento = totalDocumentos > 1;

  const onSubmit = async () => {
    if (enviandoRef.current) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErrosPorCampo({});
    setErro('');

    try {
      const payload = {
        idProcesso,
        tipoDestinatario: formFields.tipoDestinatario || null,
        email: formFields.email || null,
        assunto: formFields.assunto || null,
        copia: formFields.copia || null,
        corpoEmail: formFields.corpoEmail || null,
        idProcessoDocumento: idsSelecionados,
      };
      await enviarEmailComDocumentosProcessoService(payload);

      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      setSucesso(true);
      onClose();
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      if (isValidationException(exception)) {
        const validations = exception.response.data.validations || [];
        const errosMapeados = validations.reduce((acc, validation) => (
          CAMPOS_FORMULARIO.includes(validation.field)
            ? { ...acc, [validation.field]: validation.message }
            : acc
        ), {});

        if (Object.keys(errosMapeados).length > 0) {
          setErrosPorCampo(errosMapeados);
        } else {
          setErro(MENSAGEM_ERRO_GENERICA);
        }
      } else {
        setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Informações do E-mail</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tipo do Destinatário"
                value={formFields.tipoDestinatario}
                onChange={onChangeTipoDestinatario}
                size="small"
              >
                {TIPOS_DESTINATARIO.map(opcao => (
                  <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Para*"
                value={formFields.email}
                onChange={onChangeCampo('email')}
                error={Boolean(errosPorCampo.email)}
                helperText={errosPorCampo.email}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cópia"
                value={formFields.copia}
                onChange={onChangeCampo('copia')}
                error={Boolean(errosPorCampo.copia)}
                helperText={errosPorCampo.copia}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Assunto*"
                value={formFields.assunto}
                onChange={onChangeCampo('assunto')}
                error={Boolean(errosPorCampo.assunto)}
                helperText={errosPorCampo.assunto}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="Anexos"
                value={`${totalDocumentos} documento${maisDeUmDocumento ? 's' : ''} ${maisDeUmDocumento ? 'foram anexados' : 'foi anexado'} ao e-mail`}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Corpo do E-mail*"
                value={formFields.corpoEmail}
                onChange={onChangeCampo('corpoEmail')}
                error={Boolean(errosPorCampo.corpoEmail)}
                helperText={errosPorCampo.corpoEmail}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onSubmit} variant="contained" disabled={enviando}>Confirmar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
      <Snackbar
        open={sucesso}
        autoHideDuration={4000}
        onClose={() => setSucesso(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSucesso(false)}>
          O e-mail foi enviado com sucesso
        </Alert>
      </Snackbar>
    </>
  );
}

DialogEnviarEmailDocumentoProcesso.propTypes = {
  open: PropTypes.bool,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  allIdsDocumentos: PropTypes.instanceOf(Map),
  onClose: PropTypes.func.isRequired,
};

DialogEnviarEmailDocumentoProcesso.defaultProps = {
  open: false,
  idProcesso: null,
  allIdsDocumentos: new Map(),
};
